import { FC, useEffect, useState, useCallback } from 'react';
import { useAppContext } from '@/context/AppContext';
import { MeasureTypes } from '@/types/core/Clock';
import Stack from '@/shared/components/Stack/Stack';
import Input from '@/shared/components/Input/Input';
import Select from '@/shared/components/Select/Select';
import './BeatPanel.css';

const BeatPanel: FC = () => {
  const { clock, setBpm, setMeasure } = useAppContext();
  const [currentBeat, setCurrentBeat] = useState(1);

  const getMeasureBeats = useCallback((measure: MeasureTypes): number => {
    switch (measure) {
      case '3/4': return 3;
      case '6/8': return 6;
      case '7/8': return 7;
      case '9/8': return 9;
      case '12/8': return 12;
      default: return 4; // 4/4 time
    }
  }, []);

  useEffect(() => {
    if (!clock) return;

    let animationFrameId: number;

    const animate = () => {
      if (!clock) return;

      const currentTime = clock.getElapsedTime();
      const bps = clock.getBpm() / 60;
      const beatDuration = 1 / bps;
      const totalBeats = getMeasureBeats(clock.measure);
      
      // Calculate current beat based on elapsed time and BPM
      const beatCount = Math.floor(currentTime / beatDuration);
      const newBeat = (beatCount % totalBeats) + 1;
      
      if (newBeat !== currentBeat) {
        setCurrentBeat(newBeat);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup animation frame on unmount or when clock changes
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [clock, currentBeat, getMeasureBeats]);

  if (!clock) return null;

  const measures: MeasureTypes[] = ['4/4', '3/4', '6/8', '7/8', '9/8', '12/8'];

  return (
    <Stack direction="horizontal" gap="md" className="beat-panel">
      <Stack direction="horizontal" gap="md" align="center">
        <Input
          type="number"
          value={clock.getBpm()}
          onChange={(value) => setBpm(Math.max(20, Math.min(300, typeof value === 'number' ? value : 20)))}
          min="20"
          max="300"
          suffix="BPM"
        />
      </Stack>

      <Stack direction="horizontal" gap="md" align="center">
        <Select
          value={clock.measure}
          onChange={(e) => setMeasure(e.target.value as MeasureTypes)}
          options={measures.map(m => ({ value: m, label: m }))}
        />
      </Stack>
    </Stack>
  );
};

export default BeatPanel;
